import { WelcomePhase } from '@cf-shared/data-model/fitness-plan';

/**
 * Which parts of the Settings screen a person can actually use, and which
 * column each part is drawn in.
 *
 * This lives outside the page component so it can be unit-tested without
 * Angular, the same way `pages/subscribe/purchase-offer-policy.ts` and
 * `pages/subscribe/coach-lapse-notice-policy.ts` do it. The page itself pulls
 * in the whole Angular dependency graph and cannot be loaded by the plain-Node
 * test runner.
 *
 * Why it exists at all (deferred finding `df-be0f49fed264`): the same fact --
 * that Training and Nutrition are the parts of Settings a person cannot use
 * before their plan is generated and before they have paid -- used to be
 * written down twice, in two unrelated encodings that nothing kept in step.
 * Once as an array index ("the first two entries of `settingsSections`"), which
 * silently pointed at the wrong sections if anybody inserted a section above
 * Training. And once as two hardcoded lists of page names inside the enabled
 * check, so a new row added to Training or Nutrition stayed usable by somebody
 * who had not paid until a person remembered to type its page name into the
 * matching list. Both failures were silent and nothing tested either of them.
 *
 * Now each section states both facts about itself, and both rules below read
 * them from there.
 */

export interface SettingsItem {
  icon: string;
  /**
   * Tier 1 white-labeling (#3707): when set, the row shows this image in place
   * of the `icon` (used for the coach's logo on the "Your Coach" row).
   */
  imageUrl?: string;
  label: string;
  pageName?: string;
  action?: () => void;
  isVisible?: () => boolean;
  isEnabled?: () => boolean;
}

export interface SettingsSection {
  title: string;
  items: SettingsItem[];
  id?: string;
  /**
   * True when this section's rows only work once the welcome flow has produced
   * a training/nutrition plan. Such a section is moved out of the way while the
   * intake is still unfinished -- see `splitSettingsColumns`.
   */
  requiresGeneratedPlan?: boolean;
  /**
   * True when this section's rows are behind the paid subscription. Every row
   * in such a section is disabled for somebody without an active subscription,
   * unless that row answers for itself with its own `isEnabled`.
   *
   * Deliberately a SEPARATE field from `requiresGeneratedPlan`, even though
   * both are set on exactly the same two sections today. They are two different
   * product rules -- "unusable until the plan exists" and "behind the paywall"
   * -- and folding them into one flag would make a future section that needs
   * only one of them wrong in a way nobody would notice.
   */
  requiresSubscription?: boolean;
}

/** Everything outside the section list that the enabled rule depends on. */
export interface SettingsAccessState {
  /** Whether the user's own subscription is active (role >= AuthRole.Paid). */
  hasActiveSubscription: boolean;
  /**
   * The welcome phase read from the plan, or undefined before the page's first
   * read has resolved. The two rules in this file treat that undefined value
   * DIFFERENTLY on purpose -- see `isIntakePending` below.
   */
  welcomePhase: WelcomePhase | undefined;
  /** Page names the welcome flow allows, from `getAllowedPageDuringWelcome()`. */
  allowedPagesInWelcome: string[];
}

/**
 * Whether the intake is still unfinished, for the purpose of ARRANGING the
 * columns.
 *
 * The truthy guard covers the brief window before the page's first plan read
 * resolves, where `welcomePhase` is still undefined: that is treated as
 * not-pending so the default layout renders against the still-empty section
 * list. The enabled rule below deliberately does NOT share this guard.
 */
export function isIntakePending(welcomePhase: WelcomePhase | undefined): boolean {
  return !!welcomePhase && welcomePhase !== WelcomePhase.Done;
}

/**
 * Whether one row of the Settings screen can be tapped.
 *
 * The checks run in this order, and the order is the behaviour:
 *
 * 1. if the row answers for itself with its own `isEnabled`, that wins outright
 * 2. else if the row's section is behind the paywall and the subscription is
 *    not active, the row is disabled
 * 3. else if the welcome flow has not finished and the row opens a page, the
 *    row is enabled only when the welcome flow allows that page
 * 4. otherwise the row is enabled
 *
 * Note step 3 tests `welcomePhase !== Done` WITHOUT the truthy guard that
 * `isIntakePending` applies. So in the window before the first plan read
 * resolves, this rule treats the intake as pending while the column
 * arrangement treats it as finished. That difference is inherited from the
 * code this replaced and is kept deliberately: erring towards "disabled" is
 * the safe direction for a tap, and erring towards "default layout" is the
 * safe direction for a screen that has no sections to draw yet.
 */
export function isSettingsItemEnabled(
  section: SettingsSection,
  item: SettingsItem,
  state: SettingsAccessState,
): boolean {
  // if the row carries its own rule, use it
  if (item.isEnabled) {
    return item.isEnabled();
  }

  // if this section is behind the paywall and the subscription is not active,
  // the row is disabled
  if (section.requiresSubscription && !state.hasActiveSubscription) {
    return false;
  }

  // if the welcome flow has not finished and this row opens a page, the
  // welcome flow decides whether that page may be opened
  if (state.welcomePhase !== WelcomePhase.Done && item.pageName) {
    return state.allowedPagesInWelcome.includes(item.pageName);
  }

  return true;
}

/**
 * Split the sections into the two on-screen columns.
 *
 * While the intake is unfinished the sections that need a generated plan move
 * to the RIGHT column, so the sections the person can actually use sit first --
 * above them on a small screen, beside them on a desktop. Once the intake is
 * done the plan-dependent sections lead, on the left.
 *
 * The split is by the section's own `requiresGeneratedPlan` flag, so inserting
 * or reordering sections cannot change which ones move.
 */
export function splitSettingsColumns(
  sections: SettingsSection[],
  welcomePhase: WelcomePhase | undefined,
): { left: SettingsSection[]; right: SettingsSection[] } {
  const planDependent = sections.filter(section => section.requiresGeneratedPlan);
  const alwaysUsable = sections.filter(section => !section.requiresGeneratedPlan);

  if (isIntakePending(welcomePhase)) {
    return { left: alwaysUsable, right: planDependent };
  }
  return { left: planDependent, right: alwaysUsable };
}
